import React from 'react'
import DataDatetime from 'syft-acp-core/components/EditableTable/DataDatetime'
import * as structureTypes from 'syft-acp-core/components/EditableTable'
import { fullLocalDateTimeFormat } from 'syft-acp-core/components/FormattedDateTime'
import { IconClock } from 'syft-acp-core/components/IconClock'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'
import { formatRelative } from 'syft-acp-util/formatting'
import { CheckCircle } from '@indeed/ifl-icons'
import { noop } from 'lodash-es'
import { Box, Tag, Tooltip } from '@indeed/ifl-components'

import { Data } from './types'
import { LabelWithInfo, WAITLISTED_TOOLTIP } from 'syft-acp-core/components/LabelWithInfo'

const EM_DASH = '—'

const poolFigure = (value?: number | null) => value ?? EM_DASH

// The pool is only readable as a story: where it started, how big it ever got, and what it is now.
// `latest` is overwritten on every offer batch, so on its own — as it was displayed before — a shift
// that offered to 45 people and filled reads as if barely anyone was ever eligible.
export const CandidatePool = ({
  initial,
  peak,
  latest,
  lastUpdate,
  timezone,
}: {
  initial?: number | null
  peak?: number | null
  latest?: number | null
  lastUpdate?: string | null
  timezone?: string
}) => {
  const summary = `initial ${poolFigure(initial)} · peak ${poolFigure(peak)} · latest ${poolFigure(latest)}`

  if (!lastUpdate) {
    return <Box as="div">{summary}</Box>
  }

  return (
    <Box as="div" sx={{ display: 'flex' }}>
      <Box as="div">{`${summary} (Last update`}</Box>
      <Box as="div" sx={{ marginLeft: '-8px', marginTop: '-8px' }}>
        <DataDatetime parameters={{ timeZone: timezone }} value={lastUpdate} data={{}} onChange={noop} />
      </Box>
      <Box as="div">{' )'}</Box>
    </Box>
  )
}

const candidatePoolProps = ({ offering_stats: stats, venue }: Data) => ({
  timezone: venue?.timezone,
  initial: stats?.initial_pool_size,
  peak: stats?.max_pool_size,
  latest: stats?.workers_available,
  lastUpdate: stats?.workers_available_updated_at,
})

const fillAutomationPoolProps = ({ offering_stats: stats, venue }: Data) => ({
  timezone: venue?.timezone,
  initial: stats?.fill_automation_initial_pool_size,
  peak: stats?.fill_automation_max_pool_size,
  latest: stats?.fill_automation_workers_available,
  lastUpdate: stats?.fill_automation_workers_available_updated_at,
})

const isFilled = (data: Data) => {
  const totalSpots = data?.booking_status?.total_spots

  // A shift with no spots at all is not "filled"; `>=` rather than `===` so an overbooked shift counts.
  return !!totalSpots && (data?.booking_status?.filled_spots ?? 0) >= totalSpots
}

// `total_offers_sent > 0` is the load-bearing guard: it was never backfilled, so a shift that last
// offered before flex-core started recording it would otherwise claim "Offered to 0, now filled".
const showsOutcome = (data: Data) =>
  isFilled(data) &&
  data?.offering_stats?.workers_offered === 0 &&
  (data?.offering_stats?.total_offers_sent ?? 0) > 0

// The venue setting is what puts a job on blast, so the switch-on time answers "how long has this
// venue been on blast offers" right where the tag says it is. Older venues switched on before the
// timestamp existed have none, so the tag stands alone.
//
// The date and its relative time are composed here rather than delegated to DataDatetime: that
// component's read-only markup carries the editable-table cell padding, which only lines up next to
// a tag once it is cancelled out with negative margins.
export const BlastOffersTag = ({ enabledAt, timezone }: { enabledAt?: string | null; timezone?: string }) => {
  const [switchedOnAt] = useFormatDateTime(enabledAt ?? undefined, {
    format: { ...fullLocalDateTimeFormat, timeZone: timezone },
  })

  return (
    <Box as="div" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Tooltip label="Blast offers the whole eligible pool at once instead of in batches. A candidate pool of 0 and no next-offer time are expected.">
        <Tag status="info">Blast offers</Tag>
      </Tooltip>
      {enabledAt ? (
        <>
          <Box as="span">on since {switchedOnAt}</Box>
          <Box as="span" className="secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <IconClock />
            {formatRelative(new Date(enabledAt))}
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export const structure = [
  [
    'offering_stats.workers_offered',
    {
      header: (
        <LabelWithInfo
          label="Active offers (open now)"
          accessibleName="More information about active offers"
          info="Offers that are open right now. It counts down as they are accepted, declined, cancelled, or expire, so 0 means every offer was actioned — not that nobody was offered."
        />
      ),
      defaultValue: EM_DASH,
    },
  ],
  [
    'offering_stats.total_offers_sent',
    {
      header: 'Total offered',
      calc: (data: Data) => data?.offering_stats?.total_offers_sent ?? EM_DASH,
    },
  ],
  [
    // `RecordSection` only re-renders when a structure key's value changes. Outcome is computed
    // from booking_status (and offering_stats already keyed above); a phantom
    // `offering_stats.outcome` would miss a fill that never touches offering stats (agency / manual).
    'booking_status',
    {
      header: 'Outcome',
      hide: (data: Data) => !showsOutcome(data),
      calc: (data: Data) => (
        <Tag status="success" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="green" size="sm" aria-hidden="true" />
          <Box as="span">{`Offered to ${data?.offering_stats?.total_offers_sent}, now filled`}</Box>
        </Tag>
      ),
    },
  ],
  [
    'offering_stats.workers_waitlisted',
    {
      header: (
        <LabelWithInfo
          label="Waitlisted"
          info={WAITLISTED_TOOLTIP}
          accessibleName="More information about waitlisted workers"
        />
      ),
      calc: (data: Data) => data?.offering_stats?.workers_waitlisted ?? EM_DASH,
    },
  ],
  [
    'select_offer_type',
    {
      header: (
        <LabelWithInfo
          label="Selected offer type"
          accessibleName="More information about selected offer type"
          info="This section reflects the stats for the latest offer type selected."
        />
      ),
      // When blast offers is on, the pool / next-offer rows below read 0 / — by design (the whole
      // eligible pool is offered at once), so surface the method here to explain those values.
      calc: (data: Data) =>
        data?.offering_stats?.blast_offers_active ? (
          <BlastOffersTag enabledAt={data?.venue?.blast_offers_enabled_at} timezone={data?.venue?.timezone} />
        ) : null,
    },
  ],
  [
    // Timestamp moves on every offer batch even when the pool size does not, so RecordSection
    // still re-renders the "Last update" line.
    'offering_stats.workers_available_updated_at',
    {
      header: 'Candidate pool',
      calc: (data: Data) => <CandidatePool {...candidatePoolProps(data)} />,
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
    },
  ],
  [
    'offering_stats.next_offer_at',
    {
      header: 'Next offers will be sent at',
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
      type: structureTypes.TYPE_TIMESTAMP,
      defaultValue: EM_DASH,
    },
  ],
  [
    'offering_stats.fill_automation_active',
    {
      header: (
        <LabelWithInfo
          label="Fill automation status"
          accessibleName="More information about fill automation status"
          info="This section shows if the fill automation for the worker pool has kicked in and the remaining worker supply eligible for offers."
        />
      ),
      calc: (data: Data) =>
        data?.offering_stats?.fill_automation_active ? <Tag status="success">Active</Tag> : 'Not active',
    },
  ],
  [
    // Live count, not the timestamp: flex-core omits fill_automation_workers_available_updated_at
    // once the shift has started, so keying on the timestamp would freeze the row after start.
    'offering_stats.fill_automation_workers_available',
    {
      // flex-core only exposes fill-automation pool figures when the automation is active, so this
      // row renders dashes when it is off rather than hiding. The labelled empty slot is intentional.
      header: 'FA candidate pool',
      calc: (data: Data) => <CandidatePool {...fillAutomationPoolProps(data)} />,
      parameters: (data: Data) => ({ timeZone: data?.venue?.timezone }),
    },
  ],
]
